/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useMemo
} from 'react';
import {useDispatch} from 'react-redux';
import {Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {useUrl} from '@ion/applications/src/hooks/useUrl';
import {
  ViewDeviceTopMenu,
  ViewDeviceTopMenuCreateJobProps,
  ViewDeviceTopMenuScheduleDataSyncProps
} from '@ion/applications/src/pages/devices/ViewDeviceTopMenu';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {translate} from '@ion/core/src/locale/locale';
import {
  CRUDPermissions,
  Device,
  GenericObject
} from '@ion/core/src/types/entities';
import {copyToClipBoard} from '@ion/core/src/utils/BrowserUtils';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import CopyButton from '@ion/dls/src/components/buttons/CopyButton';
import {
  getDeviceIdentifierData as safelyGetDeviceIdentifier
} from '@ion/dls/src/editors/grids/DeviceDetailViewGrid';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {DLSTheme, IconSize} from '../types/types';
import {getClasses} from './editorStyles';
import {getSelectedDevice} from './helpers';

export interface DeviceViewDialogProps extends ViewDeviceTopMenuCreateJobProps,
  ViewDeviceTopMenuScheduleDataSyncProps {
  styleOverrides?: (theme: DLSTheme) => GenericObject;
  onCloseDialog?: (e: MouseEvent) => void;
  onRefresh?: MouseEventHandler<HTMLButtonElement>;
  selectedDevice?: Device;
  onEdit?: Function;
  onSwap?: Function;
  onAudit?: Function;
  onCreateJob?: Function;
  onMoveDevice?: Function;
  dialogTitle?: string | Node;
  children: ReactNode;
  open: boolean;
  includeEditAction?: boolean;
  hasCreateJob?: boolean;
  devicesPermissions?: CRUDPermissions;
  deviceDetail?: Device;
}

const styles = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : `${dlsColor('grey6')} !important`,
    maxWidth                       : '96%',
    minWidth                       : '96%',
    overflow                       : 'hidden',
    margin                         : 'auto',
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  paperScrollPaper : {
    maxHeight : 'calc(100% - 94px)',
    minHeight : '96vh'
  },
  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },
  titleWrapper : {
    display : 'flex'
  },
  formTitle : {
    display        : 'flex',
    justifyContent : 'left',
    alignItems     : 'flex-start',
    width          : '90%'
  },
  accountWrapper : {
    paddingRight : '10px',
    paddingLeft  : '10px',
    marginTop    : '-2px'
  },
  closeButton : {
    position  : 'absolute',
    left      : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  dialogTitle : {
    marginLeft   : '40px',
    whiteSspace  : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  },
  formDialogContent : {
    overflowX  : 'hidden',
    paddingTop : '0px'
  },
  copyLink : {
    border        : 'none',
    color         : dlsColor('aaBlue'),
    cursor        : 'pointer',
    paddingBottom : 4
  }
});

/**
 * Wrapper component for form Dialogs
 *
 * @param children             Component that is to be wrapped
 * @param dialogTitle          Dialog title
 * @param open                 should the dialog be open
 * @param onCloseDialog        callback for when the dialog requests to be closed
 * @param onCancelDialog       callback for when the user clicks the cancel button
 * @param onPrimaryButtonClick callback to execute on main CTA button click
 * @param primaryButtonLoading if true we show the button spinner
 * @param styleOverrides       function that returns styles to override the base
 *
 * ```typescript
 *
 * import {DeviceViewDialog} from '@ion/dls/src/editors/';
 *
 * <DeviceViewDialog open={open}
 *   dialogTitle='Add Asset'
 *   onCloseDialog={onCloseDialog}
 *   onCancelDialog={onCancelDialog}
 *   onPrimaryButtonClick={onPrimaryButtonClick}
 *   primaryButtonLoading={true}>
 *   <Component />
 * </DeviceViewDialog>
 *
 * ```
 *
 */
export const DeviceViewDialog = ({
  children,
  devicesPermissions,
  onCloseDialog,
  styleOverrides,
  selectedDevice,
  deviceDetail,
  onEdit,
  onSwap,
  onAudit,
  onCreateJob,
  onMoveDevice,
  onRefresh,
  onScheduleDataSync,
  open,
  hasCreateJob,
  includeEditAction = false,
  hasFilePermissions = false
}: DeviceViewDialogProps) => {
  const dialogStyles = useExtendStyles(styles, styleOverrides);
  const classes      = dialogStyles();
  const dispatch     = useDispatch();
  const currentUrl   = useUrl();
  const device       = getSelectedDevice(selectedDevice, deviceDetail);

  const title = useMemo(() => {
    if (device && !isObjectEmpty(device)) {
      const identifier = safelyGetDeviceIdentifier(device);
      return `${identifier.label} : ${identifier.value || ''}`;
    }
    return translate('common:states.loading');
  }, [selectedDevice, deviceDetail]);

  const copyValuesToClipboard = useCallback(() => {
    copyToClipBoard(`${currentUrl.getOrigin()}/devices?id=${device?.id}`);
    dispatch(toggleUserPrompt({
      message          : translate('common:clipboard.copySuccess'),
      autoHideDuration : 1000
    }));
  }, [selectedDevice, deviceDetail, currentUrl]);

  return (
    <Dialog open={open}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.titleWrapper}>
          <div className={classes.formTitle}>
            <div onClick={onCloseDialog} className={classes.closeButton}>
              <MaterialExitCloseIcon size={IconSize.medium}
                color={dlsColor('grey3')} />
            </div>
            <Typography className={classes.dialogTitle} component='h1' variant='h2'>
              {title}
            </Typography>
            <div className={classes.copyLink}>
              <CopyButton size={IconSize.medium} onCopy={copyValuesToClipboard} />
            </div>
          </div>
          <ViewDeviceTopMenu onRefresh={onRefresh}
            onEdit={onEdit}
            onSwap={onSwap}
            onScheduleDataSync={onScheduleDataSync}
            onAudit={onAudit}
            onCreateJob={onCreateJob}
            onMoveDevice={onMoveDevice}
            devicesPermissions={devicesPermissions}
            selectedDevice={selectedDevice}
            hasFilePermissions={hasFilePermissions}
            hasCreateJob={hasCreateJob}
            includeEditAction={includeEditAction} />
        </div>
      </DialogTitle>
      <DialogContent className={classes.formDialogContent}>
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default DeviceViewDialog;
